/**
 * nested-carousel plugin
 *
 * Decorative inner carousels nested inside outer slider sections — for
 * example, one per card in `sections/v3-plans-with-cards.liquid`. The
 * wrappers are marked `aria-hidden` and accept a click to advance instead
 * of being driven by keyboard navigation (assistive tech doesn't reach
 * them).
 *
 * Three responsibilities:
 *
 *   1. Pagination override — the pagination element typically lives outside
 *      the `.swiper` element, in the parent card's content area. We discover
 *      it via the closest `[data-nested-carousel-parent="true"]` ancestor
 *      and a `[data-nested-carousel-pagination="true"]` descendant.
 *
 *   2. Click-to-advance — tapping anywhere on the swiper advances to the
 *      next slide using the section's configured `data-speed`.
 *
 *   3. Pagination bullet a11y — bullets get `tabindex="-1"` and
 *      `aria-hidden="true"` so they don't pollute the tab order or
 *      announce to screen readers.
 *
 * NOTE: Touch-drag behavior is left enabled (matches existing production
 * behavior). When the nested-carousel-group plugin lands, it'll layer on
 * `allowTouchMove: false` for grouped instances.
 */

const handlersBySwiper = new WeakMap();

/**
 * Find the pagination element for a nested swiper. Returns null if the
 * markup doesn't include one — caller should leave the parser-supplied
 * pagination in place rather than crash.
 */
function findExternalPagination(swiperElement) {
  const cardContainer = swiperElement.closest('[data-nested-carousel-parent="true"]');
  if (!cardContainer) return null;
  return cardContainer.querySelector('[data-nested-carousel-pagination="true"]');
}

/**
 * Apply tabindex / aria-hidden to all current pagination bullets. Idempotent.
 */
function setBulletA11y(swiperElement) {
  const bullets = swiperElement.querySelectorAll('.swiper-pagination-bullet');
  bullets.forEach((bullet) => {
    bullet.setAttribute('tabindex', '-1');
    bullet.setAttribute('aria-hidden', 'true');
  });
}

export const nestedCarousel = {
  name: 'nested-carousel',

  matches(el) {
    return el.classList.contains('nested-swiper');
  },

  parseOptions(el, _section, options) {
    const paginationEl = findExternalPagination(el);
    if (paginationEl) {
      options.pagination = {
        el: paginationEl,
        clickable: true,
        type: 'bullets',
      };
    }
    return options;
  },

  afterInit(swiper) {
    const el = swiper.el;
    if (!el) return;

    // Decorative wrapper — never focusable.
    el.removeAttribute('tabindex');

    // Bullets exist by now: Swiper's internal `init` event fired
    // synchronously inside `new Swiper(...)` and created them.
    setBulletA11y(el);

    // Re-apply when bullets are recreated (e.g. observer-driven slide changes).
    swiper.on('paginationUpdate', () => setBulletA11y(el));

    const handler = (e) => {
      e.stopPropagation();
      e.preventDefault();

      if (!swiper.enabled || typeof swiper.slideNext !== 'function') return;

      const speed = parseInt(swiper.params.speed, 10) || 2000;
      try {
        swiper.slideNext(speed);
      } catch (err) {
        console.warn('nested-carousel: slideNext failed', err);
      }
    };

    el.addEventListener('click', handler, { passive: false });
    handlersBySwiper.set(swiper, handler);
  },

  onDestroy(swiper) {
    const handler = handlersBySwiper.get(swiper);
    if (handler && swiper.el) {
      swiper.el.removeEventListener('click', handler);
    }
    handlersBySwiper.delete(swiper);
  },
};

export default nestedCarousel;
